import { useEffect, useState } from 'react'

import type { ThemeMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { Libraries, useGoogleMapsScript } from 'use-google-maps-script'
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete'

import {
  Autocomplete,
  AutocompleteOption,
  Box,
  CheckCircleIcon,
  MapPinIcon,
  Tooltip,
} from '@hivewatch/dark'

const libraries: Libraries = ['places']

interface AddressI {
  value: string
  latitude?: number
  longitude?: number
}

export interface DarkAddressSearchIProps {
  onSelectAddress?: (address?: AddressI) => void
  defaultValue?: string
  isSubmitting?: boolean
  disabled?: boolean
  showTooltip?: boolean
  colorScheme: ThemeMode
}

/**
 * Rebrand variant of {@link AddressSearch}: loads Google Places, resolves the
 * selected address to lat/lng, and renders the dark {@link Autocomplete} UI with
 * a dark tooltip for the disabled state. The presentational combobox lives in
 * `@hivewatch/dark`; this component owns only the data/geocoding logic. The
 * light `AddressSearch` is untouched.
 */
export const DarkAddressSearch = ({
  onSelectAddress,
  defaultValue,
  isSubmitting,
  disabled = false,
  showTooltip = true,
  colorScheme,
}: DarkAddressSearchIProps) => {
  const { t } = useTranslation()
  const { isLoaded, loadError } = useGoogleMapsScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY ?? '',
    libraries,
  })
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    initOnMount: false,
    debounce: 300,
    defaultValue,
  })

  const [isDirty, setIsDirty] = useState(false)
  // If defaultValue exists, assume it's valid initially
  const [isError, setIsError] = useState(!defaultValue)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  // Initialise Places once the Google Maps script is available.
  useEffect(() => {
    if (isLoaded && !loadError) init()
  }, [isLoaded, loadError])

  useEffect(() => {
    setValue(defaultValue, false)
    if (defaultValue) {
      setIsError(false)
      setIsDirty(false)
    }
  }, [defaultValue])

  useEffect(() => {
    isSubmitting && setIsDirty(true)
  }, [isSubmitting])

  useEffect(() => {
    if (disabled && defaultValue === null) {
      setValue('')
    }
  }, [disabled])

  if (loadError) {
    return null
  }

  const options: AutocompleteOption[] =
    status === 'OK'
      ? data.map(({ description }) => ({
          value: description,
          label: description,
        }))
      : []

  // Any typing invalidates the current selection until a suggestion is chosen.
  const handleChange = (next: string) => {
    setValue(next)
    onSelectAddress?.(undefined)
    setIsError(true)
  }

  const handleSelect = async (option: AutocompleteOption) => {
    setValue(option.value, false)
    clearSuggestions()
    try {
      const results = await getGeocode({ address: option.value })
      const { lat, lng } = await getLatLng(results[0])
      onSelectAddress?.({ value: option.value, latitude: lat, longitude: lng })
      setIsError(false)
    } catch {
      setIsError(true)
    }
  }

  const isValid = !isError && !disabled

  return (
    <Box
      onMouseEnter={(e) => {
        if (disabled && showTooltip) setAnchorEl(e.currentTarget)
      }}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <Autocomplete
        colorScheme={colorScheme}
        disabled={!ready || disabled}
        hasWarning={isError && isDirty}
        leftIcon={<MapPinIcon size={16} />}
        onBlur={() => setIsDirty(true)}
        onChange={handleChange}
        onSelect={handleSelect}
        options={options}
        placeholder={t('common:address_search_placeholder')}
        rightAdornment={isValid ? <CheckCircleIcon size={18} /> : undefined}
        value={value}
        warningText={t('common:address_search_error')}
      />
      {anchorEl && (
        <Tooltip
          anchorEl={anchorEl}
          colorScheme={colorScheme}
          label={t('common:address_search_contact_hivewatch')}
        />
      )}
    </Box>
  )
}
